import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3-geo';

import { ReactComponent as EuropeSVG } from '../data/europe-mercator-lowpoly.svg';

import countryMap from '../data/country-map.json';
import coords from '../data/coords.json';
// import jsonDojos from '../data/dojos.json';
import { ReactComponent as CanariasSVG } from '../data/recurso-canarias.svg';
import '../assets/css/mapa.css';

export default function MapaEuropaSVG({ selectedCountry, setSelectedCountry, selectedProvincia, setSelectedProvincia, Dojos }) {
  const svgRef = useRef(null);
  const svgCanariasRef = useRef(null);
  const selectedCountryRef = useRef(selectedCountry);
  const selectedProvRef = useRef(selectedProvincia);
  const DojosRef = useRef(Dojos);
  const [initialViewBox, setInitialViewBox] = useState(null);
  const [canariasLoaded, setCanariasLoaded] = useState(false);

  const projection = d3.geoMercator()
    .center([-3.5, 40]) // centrar en España
    .translate([286.0, 196.6]) // mover al centro en SVG
    .scale(92); // ajustar hasta que cuadre

  const projection_canarias = d3.geoMercator()
    .center([-15.5, 28.5])  // centrar en Canarias
    .translate([3.9, 1.5]) // mover al centro en SVG
    .scale(130); // ajustar hasta que cuadre

  // Helpers para animar el viewBox (suavizado con requestAnimationFrame)
  const parseViewBox = (vb) => {
    if (!vb || typeof vb !== 'string') return null;
    const parts = vb.trim().split(/\s+/).map(Number);
    if (parts.length !== 4 || parts.some((n) => Number.isNaN(n))) return null;
    return parts;
  };

  const vbToString = (arr) => `${arr[0]} ${arr[1]} ${arr[2]} ${arr[3]}`;

  // easing cubic in-out
  const easeCubicInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  const animateViewBox = (svgEl, fromStr, toStr, duration = 900, delay = 300) => {
    const from = parseViewBox(fromStr);
    const to = parseViewBox(toStr);
    if (!svgEl || !from || !to) {
      // fallback inmediato
      try {
        svgEl && svgEl.setAttribute('viewBox', toStr);
      } catch (e) { }
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const startAnimation = () => {
        const start = performance.now();
        const step = (now) => {
          const t = Math.min(1, (now - start) / duration);
          const e = easeCubicInOut(t);
          const cur = from.map((f, i) => f + (to[i] - f) * e);
          try {
            svgEl.setAttribute('viewBox', vbToString(cur));
          } catch (err) {
            // ignore
          }
          if (t < 1) requestAnimationFrame(step);
          else resolve();
        };
        requestAnimationFrame(step);
      };

      if (delay > 0) {
        setTimeout(startAnimation, delay);
      } else {
        startAnimation();
      }
    });
  };

  function EliminarCirclesEspecificos(svgEl) {
    const generalCircles = svgEl.querySelectorAll(`.marker-circle-specific, .marker-text-specific`);
    generalCircles.forEach(c => c.classList.add('oculto'));
  }

  function MostrarCirclesEspecificos(svgEl, code) {
    const generalCircles = svgEl.querySelectorAll(`.marker-circle-specific-${code}, .marker-text-specific-${code}`);
    generalCircles.forEach(c => c.classList.remove('oculto'));
  }

  function removeGeneralCircles(svgEl) {
    const generalCircles = svgEl.querySelectorAll('.marker-circle, .marker-text');
    generalCircles.forEach(c => c.classList.add('oculto'));
  }

  function showGeneralCircles(svgEl) {
    const generalCircles = svgEl.querySelectorAll('.marker-circle, .marker-text');
    generalCircles.forEach(c => c.classList.remove('oculto'));
  }

  function addDetailedDojoCircles(svgEl, dojosInCountry, proj, code) {
    const generalCircles = svgEl.querySelectorAll(`.marker-circle-specific-${code}, .marker-text-specific-${code}`);
    if (generalCircles.length>0){
      MostrarCirclesEspecificos(svgEl, code)
      return;
    }
    // Si la proyección aún no está lista, no hacemos nada.
    if (!proj) {
      console.error("La proyección no está lista.");
      return;
    }
    if (!coords[code]) {
      console.error("País desconocido.");
      return;
    }

    // Contar dojos por provincia
    const dojosByPovincia = {};
    dojosInCountry.forEach(d => {
      const country = d.cod_prov; // 01, 02, etc.
      if (!dojosByPovincia[country]) dojosByPovincia[country] = 0;
      dojosByPovincia[country]++;
    });

    Object.keys(dojosByPovincia).forEach(d => {
      // Primero, busca las coordenadas de forma segura
      const cityCoords = coords[code];
      // Si las coordenadas existen, dibuja el círculo.
      if (cityCoords) {
        // Añadir manualmente en CANARIAS
        if (code === 'ES' && coords[code][d].lat < 29){
          const svgcanarias = svgCanariasRef.current?.querySelector('svg');
          if (!svgcanarias) return;
          // Convierte las coordenadas geográficas a coordenadas del SVG
          const svgCoords = projection_canarias([coords[code][d].lon, coords[code][d].lat]);
          const x = svgCoords[0];
          const y = svgCoords[1];
          const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          c.setAttribute('class', `marker-circle-specific-${code} marker-circle-specific`);
          c.setAttribute('cx', x);
          c.setAttribute('cy', y);
          c.setAttribute('r', 0.6);
          c.setAttribute('fill', '#444');
          c.setAttribute('stroke', '#666');
          c.setAttribute('stroke-width', 0.1);
          const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
          title.textContent = coords[code][d].nombre;
          c.appendChild(title);
          // Para cada círculo
          c.onclick = (ev) => {
            ev.stopPropagation();
            if (selectedProvRef.current === d){
               c.classList.remove('selected');
               setSelectedProvincia(null);
               return;
            }
            // console.log("BOTON PROVINCIA:", d.cod_prov);
            c.classList.add('selected');
            // elimina la clase selected de los demás círculos
            svgEl.querySelectorAll('.marker-circle-specific.selected').forEach(s => {
              if (s !== c) s.classList.remove('selected');
            });
            setSelectedProvincia(d);
          };
          svgcanarias.appendChild(c);
          // Añadir texto centrado con la cantidad de dojos
          const count = dojosByPovincia[d] || 0;
          const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
          text.setAttribute('class', `marker-text-specific-${code} marker-text-specific`);
          text.setAttribute('x', x);
          text.setAttribute('y', y);
          text.setAttribute('fill', 'white');
          text.setAttribute('text-anchor', 'middle');
          // vertical centering; si el navegador no soporta 'dominant-baseline' se puede ajustar con dy
          text.setAttribute('dominant-baseline', 'central');
          text.setAttribute('font-size', '0.6');
          text.setAttribute('font-weight', 'bold');
          text.style.pointerEvents = 'none'; // que el texto no interfiera con clicks
          text.textContent = String(count);
          svgcanarias.appendChild(text);
        }else{
          // Convierte las coordenadas geográficas a coordenadas del SVG
          const svgCoords = proj([coords[code][d].lon, coords[code][d].lat]);
          const x = svgCoords[0];
          const y = svgCoords[1];
          // CENTRO
          // 287.7050018310547 x
          // 196.65998077392578 y 
          // console.log(`Coordenadas SVG (x, y) de ${d.ciudad}: (${x}, ${y})`);
          const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          // do not add 'temp' class so opacity/transition animations can be applied cleanly
          c.setAttribute('class', `marker-circle-specific-${code} marker-circle-specific`);
          c.setAttribute('cx', x);
          c.setAttribute('cy', y);
          c.setAttribute('r', 0.6);
          c.setAttribute('fill', '#444');
          c.setAttribute('stroke', '#666');
          c.setAttribute('stroke-width', 0.1);
          const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
          title.textContent = coords[code][d].nombre;
          c.appendChild(title);

          // Para cada círculo
          c.onclick = (ev) => {
            ev.stopPropagation();
            if (selectedProvRef.current === d){
               c.classList.remove('selected');
               setSelectedProvincia(null);
               return;
            }
            // console.log("BOTON PROVINCIA:", d.cod_prov);
            c.classList.add('selected');
            // elimina la clase selected de los demás círculos
            svgEl.querySelectorAll('.marker-circle-specific.selected').forEach(s => {
              if (s !== c) s.classList.remove('selected');
            });
            setSelectedProvincia(d);
          };

          svgEl.appendChild(c);

          // Añadir texto centrado con la cantidad de dojos
          const count = dojosByPovincia[d] || 0;
          const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
          text.setAttribute('class', `marker-text-specific-${code} marker-text-specific`);
          text.setAttribute('x', x);
          text.setAttribute('y', y);
          text.setAttribute('fill', 'white');
          text.setAttribute('text-anchor', 'middle');
          // vertical centering; si el navegador no soporta 'dominant-baseline' se puede ajustar con dy
          text.setAttribute('dominant-baseline', 'central');
          text.setAttribute('font-size', '0.6');
          text.setAttribute('font-weight', 'bold');
          text.style.pointerEvents = 'none'; // que el texto no interfiera con clicks
          text.textContent = String(count);
          svgEl.appendChild(text);
        }

      }
    });
  }

  useEffect(() => { selectedCountryRef.current = selectedCountry }, [selectedCountry]);

  useEffect(() => { selectedProvRef.current = selectedProvincia }, [selectedProvincia]);

  useEffect(() => { DojosRef.current = Dojos }, [Dojos]);

  useEffect(() => {
    const svgEl = svgRef.current?.querySelector('svg');
    if (!svgEl) return;
    setSelectedProvincia(null);
    const paths = Array.from(svgEl.querySelectorAll('path, polygon, polyline'));
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    // Contar dojos por país
    const dojosByCountry = {};
    DojosRef.current.filter(d => d.visible === true).forEach(d => {
      const country = d.country; // ES, UA, etc.
      if (!dojosByCountry[country]) dojosByCountry[country] = 0;
      dojosByCountry[country]++;
    });
    // recorro todos los paths, pero solo los que estén en countryMap y que tengan mas de 1 dojo
    paths.forEach((p, idx) => {
      const key = String(idx);
      if (Object.prototype.hasOwnProperty.call(countryMap, key) && dojosByCountry[countryMap[key].code]>0) {
        try {
          const bbox = p.getBBox();
          minX = Math.min(minX, bbox.x);
          minY = Math.min(minY, bbox.y);
          maxX = Math.max(maxX, bbox.x + bbox.width);
          maxY = Math.max(maxY, bbox.y + bbox.height);
        } catch (e) {
          // ignorar errores en algún path raro
        }
      }
    });

    // calculamos el viewBox solo con los países clicables
    if (minX < Infinity && minY < Infinity) {
      const margin = 5; // espacio extra
      const x = minX - margin;
      const y = minY - margin;
      const w = maxX - minX + margin * 2;
      const h = maxY - minY + margin * 2;
      const vb = `${x} ${y} ${w} ${h}`;
      svgEl.setAttribute('viewBox', vb);
      setInitialViewBox(vb);
    }

    

    // ahora añadimos los listeners
    paths.forEach((p, idx) => {
      try {
        p.classList.add('country');
        const key = String(idx);
        if (Object.prototype.hasOwnProperty.call(countryMap, key) && dojosByCountry[countryMap[key].code]>0) {
          p.classList.add('selectable');
          // const bbox = p.getBBox(); // obtiene x, y, width, height
          // const cx = bbox.x + bbox.width / 2;
          // const cy = bbox.y + bbox.height / 2;
          const svgCoords = projection([coords['countryCenters'][countryMap[key].code].lon, coords['countryCenters'][countryMap[key].code].lat]);
          const cx = svgCoords[0];
          const cy = svgCoords[1];
          // crear el círculo
          const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          circle.setAttribute('class', 'marker-circle');
          circle.setAttribute('cx', cx);
          circle.setAttribute('cy', cy);
          circle.setAttribute('r', 1.5);
          circle.setAttribute('fill', '#d9534f');
          circle.setAttribute('stroke', '#fff');
          circle.setAttribute('stroke-width', 0.2);

          // Añadir el círculo al DOM
          p.parentNode.appendChild(circle);

          // Para cada círculo
          circle.onclick = (ev) => {
           ev.stopPropagation();
            if (selectedCountryRef.current === code) return;
            if (code !== 'ES') setCanariasLoaded(false);
            setSelectedProvincia(null);
            setSelectedCountry(code);
            // eliminar los puntos generales
            removeGeneralCircles(svgEl);
            EliminarCirclesEspecificos(svgEl);

            let bbox;
            // Si no, usa el bbox del elemento
            bbox = p.getBBox();
            const margin = 5;
            const x = bbox.x - margin;
            const y = bbox.y - margin;
            const w = bbox.width + margin * 2;
            const h = bbox.height + margin * 2;

            const target = `${x} ${y} ${w} ${h}`;
            const current = svgEl.getAttribute('viewBox') || '';
            animateViewBox(svgEl, current, target, 450).then(() => {
              // añadir puntos detallados
              const dojosInCountry = DojosRef.current.filter(d => d.visible === true).filter(d => d.country === code);
              addDetailedDojoCircles(svgEl, dojosInCountry, projection, code);
              (code === 'ES') ? setCanariasLoaded(true) : setCanariasLoaded(false);
            });
          };

          // Añadir texto centrado con la cantidad de dojos
          const count = dojosByCountry[countryMap[key].code] || 0;
          const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
          text.setAttribute('class', 'marker-text');
          text.setAttribute('x', cx);
          text.setAttribute('y', cy);
          text.setAttribute('fill', '#fff');
          text.setAttribute('text-anchor', 'middle');
          text.setAttribute('dominant-baseline', 'central');
          text.setAttribute('font-size', '1.5');
          text.setAttribute('font-weight', 'bold');
          text.style.pointerEvents = 'none'; // que el texto no interfiera con clicks
          text.textContent = String(count);

          p.parentNode.appendChild(text);

          const entry = countryMap[key];
          const code =
            entry && typeof entry === 'object'
              ? entry.code
              : typeof entry === 'string'
                ? entry
                : null;
          if (code) {
            p.setAttribute('data-code', code);
            p.setAttribute('role', 'button');
            p.setAttribute('tabindex', '0');

            // Click con zoom
            p.onclick = (ev) => {
              ev.stopPropagation();
              if (selectedCountryRef.current === code) return;
              if (code !== 'ES') setCanariasLoaded(false);
              setSelectedProvincia(null);
              setSelectedCountry(code);
              // eliminar los puntos generales
              removeGeneralCircles(svgEl);
              EliminarCirclesEspecificos(svgEl);

              let bbox;
              // Si no, usa el bbox del elemento
              bbox = p.getBBox();
              const margin = 5;
              const x = bbox.x - margin;
              const y = bbox.y - margin;
              const w = bbox.width + margin * 2;
              const h = bbox.height + margin * 2;

              const target = `${x} ${y} ${w} ${h}`;
              const current = svgEl.getAttribute('viewBox') || '';
              animateViewBox(svgEl, current, target, 450).then(() => {
                // añadir puntos detallados
                const dojosInCountry = DojosRef.current.filter(d => d.visible === true).filter(d => d.country === code);
                addDetailedDojoCircles(svgEl, dojosInCountry, projection, code);
                (code === 'ES') ? setCanariasLoaded(true) : setCanariasLoaded(false);
              });
            };
          }
        }
      } catch (e) {
        // ignore errors per-path
      }
    });
    // eslint-disable-next-line
  }, []);

  const resetView = () => {
    const svgEl = svgRef.current?.querySelector('svg');
    if (!svgEl || !initialViewBox) return;
    setSelectedProvincia(null);
    setCanariasLoaded(false);
    EliminarCirclesEspecificos(svgEl);
    const current = svgEl.getAttribute('viewBox') || '';
    animateViewBox(svgEl, current, initialViewBox, 450).then(() => {
      setSelectedCountry(null);
      showGeneralCircles(svgEl);
    });
  };


  return (
    <div className="map-frame" style={{ position: 'relative', width: 'calc(60% - 5px)', height: '100%' }}>
      <div className="map-wrapper-svg" ref={svgRef} style={{ width: '100%', height: '100%' }}>
        <EuropeSVG preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: '600px' }} />
      </div>
      <div
        className={`canarias-frame ${canariasLoaded && 'visible'}`} ref={svgCanariasRef}
      >
        <CanariasSVG className="selectable" style={{ width: '100%', height: '100%' }} />
      </div>
      {/* Botón reset */}
      {selectedCountry && (
        <button
          className='button cta'
          onClick={resetView}
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
          }}
        >
          Volver a vista completa
        </button>
      )}
    </div>
  );
}
